import { createMemo, createResource, createSignal, For, Show } from "solid-js";
import { useApi, isOk } from "../api";
import { firstError } from "../api/envelope";
import { getMapCacheGetImageUrl } from "../api/generated/map-cache";
import type { CachedMapImageDto } from "../api/generated/schemas";
import { useTranslation } from "../hooks/useTranslation";
import { useToast } from "../components/MessageBox";
import Text from "../components/Text";
import MapImage from "../components/MapImage";
import { Pagination } from "../components/Pagination";
import { SearchInput } from "../components/SearchInput";
import { EmptyState } from "../components/EmptyState";
import { SkeletonCard } from "../components/Skeleton";
import { formatDateTime } from "../services/dateFormat";
import { formatInteger } from "../services/numberFormat";
import { downloadFromApi } from "../services/downloadFile";
import styles from "./MapCache.module.css";

const PAGE_SIZE = 48;

export default function MapCache() {
    const api = useApi();
    const { t } = useTranslation();
    const toast = useToast();

    const [search, setSearch] = createSignal("");
    const [page, setPage] = createSignal(0);

    const [maps] = createResource(
        () => ({ search: search(), page: page() }),
        async (query) => {
            const res = await api.mapCache.list({
                search: query.search || undefined,
                page: query.page,
                pageSize: PAGE_SIZE,
            });
            if (!isOk(res.data)) {
                toast?.error(firstError(res.data) ?? t("MAP_CACHE_LOAD_FAILED"));
                return { maps: [] as CachedMapImageDto[], total: 0 };
            }
            return res.data.value!;
        }
    );

    const total = () => maps.latest?.total ?? 0;
    const totalPages = createMemo(() => Math.max(1, Math.ceil(total() / PAGE_SIZE)));

    const download = async (map: CachedMapImageDto) => {
        const url = getMapCacheGetImageUrl(encodeURIComponent(map.map3), { v: map.pixelHash });
        const failed = await downloadFromApi(url, `${map.map3}.png`);
        if (failed) toast?.error(t("MAP_CACHE_DOWNLOAD_FAILED"));
    };

    return (
        <div class={styles.container}>
            <div class={styles.headerRow}>
                <div class={styles.titleBlock}>
                    <h1><Text val="MAP_CACHE_TITLE" /></h1>
                    <p class={styles.subtitle}><Text val="MAP_CACHE_SUBTITLE" /></p>
                </div>
                <span class={styles.entryCount}>{t("MAP_CACHE_COUNT", formatInteger(total()))}</span>
            </div>

            <div class={`fcard ${styles.toolbar}`}>
                <div class={styles.searchWrap}>
                    <SearchInput
                        value={search()}
                        onInput={(value) => { setSearch(value); setPage(0); }}
                        placeholder={t("MAP_CACHE_SEARCH_PLACEHOLDER")}
                        ariaLabel={t("MAP_CACHE_SEARCH_PLACEHOLDER")}
                    />
                </div>
            </div>

            <Show
                when={maps.state !== "pending"}
                fallback={<div class={styles.grid}><For each={Array(8).fill(0)}>{() => <SkeletonCard />}</For></div>}
            >
                <Show
                    when={(maps.latest?.maps.length ?? 0) > 0}
                    fallback={
                        <EmptyState
                            illustration="generic"
                            title={t(search() ? "MAP_CACHE_EMPTY_SEARCH_TITLE" : "MAP_CACHE_EMPTY_TITLE")}
                            hint={t(search() ? "MAP_CACHE_EMPTY_SEARCH_HINT" : "MAP_CACHE_EMPTY_HINT")}
                        />
                    }
                >
                    <div class={styles.grid}>
                        <For each={maps.latest!.maps}>
                            {(map) => <MapCard map={map} onDownload={() => download(map)} />}
                        </For>
                    </div>
                    <Pagination page={page()} totalPages={totalPages()} onChange={setPage} />
                </Show>
            </Show>
        </div>
    );
}

function MapCard(props: { map: CachedMapImageDto; onDownload: () => void }) {
    const { t } = useTranslation();

    return (
        <div class={`fcard ${styles.card}`}>
            <MapImage map3={props.map.map3} pixelHash={props.map.pixelHash} alt={props.map.displayName ?? t("MAP_CACHE_UNNAMED")} />
            <Show when={props.map.displayName} fallback={<span class={`${styles.cardName} ${styles.cardUnnamed}`}><Text val="MAP_CACHE_UNNAMED" /></span>}>
                <span class={styles.cardName} title={props.map.displayName!}>{props.map.displayName}</span>
            </Show>
            <span class={styles.cardMap3} title={props.map.map3}>{props.map.map3}</span>
            <div class={styles.cardFooter}>
                <span title={t("MAP_CACHE_LAST_SEEN")}>{formatDateTime(props.map.lastSeenAt)}</span>
                <button type="button" class={`btn btn-secondary btn-sm ${styles.download}`} onClick={props.onDownload}>
                    <i class="fa-solid fa-download" /> <Text val="MAP_CACHE_DOWNLOAD" />
                </button>
            </div>
        </div>
    );
}
